import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { adminApi, apiErrorMessage } from "../../api/adminApi";
import { usePermissions } from "../../permissions/usePermissions";
import Can from "../../permissions/Can";
import "../../main.scss";
import Tooltip from "../../components/Tooltip";

const emptyForm = { username: "", password: "", full_name: "", role_id: "", is_active: 1 };

export default function Users() {
  const { user: me } = usePermissions();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [pwResetId, setPwResetId] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([adminApi.getUsers(), adminApi.getRoles()]);
      setUsers(u);
      setRoles(r);
    } catch (e) {
      toast.error(apiErrorMessage(e, "فشل تحميل المستخدمين"));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      username: u.username,
      password: "",
      full_name: u.full_name,
      role_id: u.role_id || "",
      is_active: u.is_active ? 1 : 0,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.username.trim()) {
      toast.error("أكمل الحقول المطلوبة");
      return;
    }
    if (!editing && form.password.length < 6) {
      toast.error("كلمة المرور 6 أحرف على الأقل");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await adminApi.updateUser(editing.id, {
          full_name: form.full_name.trim(),
          role_id: form.role_id || null,
          is_active: form.is_active,
        });
        toast.success("تم تحديث المستخدم");
      } else {
        await adminApi.createUser({
          username: form.username.trim(),
          password: form.password,
          full_name: form.full_name.trim(),
          role_id: form.role_id || null,
          is_active: form.is_active,
        });
        toast.success("تم إنشاء المستخدم");
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
    setSaving(false);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`حذف المستخدم "${u.full_name}" نهائياً؟`)) return;
    try {
      await adminApi.deleteUser(u.id);
      toast.success("تم حذف المستخدم");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("كلمة المرور 6 أحرف على الأقل");
      return;
    }
    try {
      await adminApi.resetPassword(pwResetId, newPassword);
      toast.success("تم تعيين كلمة المرور الجديدة");
      setPwResetId(null);
      setNewPassword("");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const roleName = (id) => roles.find((r) => r.id === id)?.name || "—";

  return (
    <div className="page users-page">
      <div className="page__header">
        <div>
          <h1 className="page__title">إدارة المستخدمين</h1>
          <p className="page__sub">حسابات الدخول الفردية للوحة التحكم وأدوارها</p>
        </div>
        <Can module="users" action="add">
          <button className="btn btn--primary" onClick={openAdd}>
            <i className="fa-solid fa-plus"></i> مستخدم جديد
          </button>
        </Can>
      </div>

      {loading ? (
        <div className="page__loading">جاري التحميل...</div>
      ) : (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>الاسم الكامل</th>
                <th>اسم المستخدم</th>
                <th>الدور</th>
                <th>الحالة</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className={u.is_active ? "" : "row--inactive"}>
                  <td data-label="الاسم">{u.full_name}</td>
                  <td data-label="المستخدم" dir="ltr">{u.username}</td>
                  <td data-label="الدور">
                    <span className="badge">{roleName(u.role_id)}</span>
                  </td>
                  <td data-label="الحالة">
                    <span className={`status-dot ${u.is_active ? "on" : "off"}`}>
                      {u.is_active ? "نشط" : "موقوف"}
                    </span>
                  </td>
                  <td data-label="إجراءات">
                    <div className="row-actions">
                      <Can module="users" action="edit">
                        <Tooltip content="تعديل">
                          <button
                            className="icon-btn"
                         
                            onClick={() => openEdit(u)}
                          >
                            <i className="fa-solid fa-pen"></i>
                          </button>
                        </Tooltip>
                        <Tooltip content="تعيين كلمة مرور">
                          <button
                            className="icon-btn"
                         
                            onClick={() => {
                              setPwResetId(u.id);
                              setNewPassword("");
                            }}
                          >
                            <i className="fa-solid fa-key"></i>
                          </button>
                        </Tooltip>
                      </Can>
                      <Can module="users" action="delete">
                        {!(me && !me.isSuperAdmin && u.id === me.id) && (
                          <Tooltip content="حذف">
                            <button
                              className="icon-btn danger"
                           
                              onClick={() => handleDelete(u)}
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </Tooltip>
                        )}
                      </Can>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="empty-row">
                    لا يوجد مستخدمون بعد — أنشئ أول حساب
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? "تعديل المستخدم" : "مستخدم جديد"}</h3>
            <form onSubmit={handleSave}>
              {!editing && (
                <label>
                  اسم المستخدم (للدخول)
                  <input
                    dir="ltr"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="username"
                    required
                  />
                </label>
              )}
              <label>
                الاسم الكامل
                <input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="مثال: أحمد محمد"
                  required
                />
              </label>
              {!editing && (
                <label>
                  كلمة المرور
                  <input
                    type="password"
                    dir="ltr"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="6 أحرف على الأقل"
                    required
                  />
                </label>
              )}
              <label>
                الدور
                <select
                  value={form.role_id}
                  onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                >
                  <option value="">بدون دور (لا صلاحيات)</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked ? 1 : 0 })
                  }
                />
                حساب نشط
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setShowModal(false)}
                >
                  إلغاء
                </button>
                <button type="submit" className="btn btn--primary" disabled={saving}>
                  {saving ? "جاري الحفظ..." : "حفظ"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pwResetId && (
        <div className="modal-overlay" onClick={() => setPwResetId(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>تعيين كلمة مرور جديدة</h3>
            <form onSubmit={handleResetPassword}>
              <label>
                كلمة المرور الجديدة
                <input
                  type="password"
                  dir="ltr"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="6 أحرف على الأقل"
                  required
                />
              </label>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setPwResetId(null)}
                >
                  إلغاء
                </button>
                <button type="submit" className="btn btn--primary">
                  تعيين
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .users-page { padding: 20px; max-width: 1100px; margin: 0 auto; }
        .page__header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 18px; flex-wrap: wrap; }
        .page__title { margin: 0 0 4px; font-size: 22px; color: var(--text); }
        .page__sub { margin: 0; color: var(--text-muted); font-size: 13px; }
        .page__loading { text-align: center; padding: 60px; color: var(--text-muted); }
        .table-card { background: var(--card); border: 1px solid var(--border); border-radius: 6px; overflow: hidden; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .data-table th { background: rgba(13,35,32,0.04); color: var(--text-muted); padding: 10px 12px; text-align: right; font-weight: 400; border-bottom: 1px solid var(--border); }
        .data-table td { padding: 10px 12px; border-bottom: 1px solid var(--border); color: var(--text); }
        .data-table tr:last-child td { border-bottom: none; }
        .row--inactive td { opacity: 0.55; }
        .badge { background: rgba(40,145,127,0.12); color: var(--accent-strong, #1d7567); padding: 3px 10px; border-radius: 6px; font-size: 12px; }
        .status-dot { font-size: 12px; }
        .status-dot.on { color: #16a34a; }
        .status-dot.off { color: #dc2626; }
        .row-actions { display: flex; gap: 6px; }
        .icon-btn { width: 32px; height: 32px; border-radius: 6px; border: 1px solid var(--border); background: var(--card); color: var(--text-muted); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
        .icon-btn:hover { color: var(--accent-strong, #1d7567); border-color: var(--accent); }
        .icon-btn.danger:hover { color: #dc2626; border-color: #dc2626; }
        .empty-row { text-align: center; color: var(--text-muted); padding: 30px !important; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(13,35,32,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 16px; }
        .modal-card { background: var(--card); border-radius: 6px; padding: 22px; width: 100%; max-width: 440px; box-shadow: 0 24px 60px rgba(0,0,0,0.18); }
        .modal-card h3 { margin: 0 0 16px; color: var(--text); font-size: 17px; }
        .modal-card label { display: block; margin-bottom: 12px; font-size: 13px; color: var(--text); }
        .modal-card input, .modal-card select { width: 100%; margin-top: 6px; padding: 10px 12px; border: 1px solid var(--border); border-radius: 6px; background: var(--bg); color: var(--text); font-size: 14px; }
        .check-row { display: flex !important; align-items: center; gap: 8px; }
        .check-row input { width: auto; margin: 0; }
        .modal-actions { display: flex; justify-content: flex-start; gap: 10px; margin-top: 18px; }
        @media (max-width: 640px) {
          .data-table thead { display: none; }
          .data-table tr { display: block; border-bottom: 1px solid var(--border); padding: 8px 0; }
          .data-table td { display: flex; justify-content: space-between; align-items: center; border: none; padding: 6px 12px; }
          .data-table td::before { content: attr(data-label); color: var(--text-muted); font-size: 12px; }
        }
      `}</style>
    </div>
  );
}
